import React, { useEffect, useRef, useState } from 'react';
import commonResource from '../../locales/th/common.json';
import { UserComment } from '../../queries/contentPage/commentList';
import { useMutationSubmitComment } from '../../services/comment';
import { Button } from '../global/Button';
import { ButtonGroup, CancelButton, FormWrapper } from './CommentForm.style';
import { CommentFormInput } from './CommentFormInput';
import { CommentFormTextArea } from './CommentFormTextArea';
import { sanitize } from '../../common/helpers/utility/sanitizer';
import { HTTP_RESPONSE_CODES } from '../../common/helpers/constants';

interface CommentFormProps {
  profile: { userName: string; email: string } | null;
  onFinish: (comment: UserComment) => void;
  onError: (errorCode: number) => void;
  onCancel?: () => void;
  parentCommentId: number;
  postId: number;
}

const CommentForm: React.FC<CommentFormProps> = ({
  profile,
  parentCommentId,
  postId,
  onFinish,
  onError,
  onCancel,
}) => {
  const [submitComment] = useMutationSubmitComment();
  const [emailInput, setEmailInput] = useState('');
  const [userNameInput, setUserNameInput] = useState('');
  const [contentInput, setContentInput] = useState('');
  const [isAlwaysShowError, setAlwaysShowError] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isFormValid, setFormValid] = useState(true);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const isValid = formRef.current?.checkValidity();
    setFormValid(isValid);
  }, [emailInput, userNameInput, contentInput]);

  const resetFormFields = () => {
    setEmailInput('');
    setUserNameInput('');
    setContentInput('');
    setAlwaysShowError(false);
  };

  const onFormSubmit = async () => {
    if (!isFormValid) {
      setAlwaysShowError(true);
      return;
    }
    try {
      setSubmitting(true);
      const response = await submitComment({
        variables: {
          authorEmail: sanitize(profile ? profile.email : emailInput),
          authorName: sanitize(profile ? profile.userName : userNameInput),
          content: sanitize(contentInput),
          postId,
          parentId: parentCommentId,
        },
      });

      const postComment = response.data.postComment;
      if (postComment && postComment.code === HTTP_RESPONSE_CODES.OK) {
        resetFormFields();
        onFinish(postComment);
      } else {
        onError(postComment?.code ?? HTTP_RESPONSE_CODES.ServerError);
      }
    } catch (e) {
      console.log('Error when get comment query in commentItem component:', e);
      onError(HTTP_RESPONSE_CODES.ServerError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormWrapper ref={formRef} role="form">
      <CommentFormTextArea
        name="content"
        placeholder={commonResource.comment.joinTheDiscussion}
        rows={3}
        value={contentInput}
        errorMessage={commonResource.comment.pleaseInputYourMessage}
        onChange={(e) => {
          setContentInput(e.currentTarget.value);
        }}
        alwaysShowError={isAlwaysShowError}
        focusOnMount
        readOnly={isSubmitting}
        required
        data-test="comment-content-input"
      />
      {!profile && (
        <>
          <CommentFormInput
            label={commonResource.comment.name}
            alwaysShowError={isAlwaysShowError}
            type="text"
            required
            placeholder={commonResource.comment.yourName}
            name="authorName"
            value={userNameInput}
            onChange={(e) => {
              setUserNameInput(e.currentTarget.value);
            }}
            readOnly={isSubmitting}
            errorMessage={commonResource.comment.pleaseInputYourName}
            data-test="comment-user-name-input"
          />
          <CommentFormInput
            type="email"
            required
            placeholder={commonResource.comment.yourEmail}
            name="authorEmail"
            value={emailInput}
            onChange={(e) => {
              setEmailInput(e.currentTarget.value);
            }}
            alwaysShowError={isAlwaysShowError}
            label={commonResource.comment.email}
            readOnly={isSubmitting}
            errorMessage={commonResource.comment.pleaseInputYourEmail}
            data-test="comment-user-email-input"
          />
        </>
      )}
      <ButtonGroup>
        <Button
          theme="ghost"
          onClick={onFormSubmit}
          disabled={isSubmitting || !isFormValid}
          type="button"
          data-test="comment-submit-btn"
        >
          {commonResource.comment.replyComment}
        </Button>
        {onCancel && (
          <CancelButton
            tabIndex={0}
            onClick={onCancel}
            data-test="comment-cancel-btn"
          >
            {commonResource.comment.cancel}
          </CancelButton>
        )}
      </ButtonGroup>
    </FormWrapper>
  );
};

export default CommentForm;
